<template>
  <div class="address-input-container">
    <!-- ラベル（住所のみ） -->
    <FormLabel :label="addressLabel" :isRequired="isRequired && !readOnly" />

    <!-- 入力フィールド（横並び） -->
    <div class="address-input-fields" :class="{ 'has-error': errorMessage }">
      <!-- readOnly 時は郵便番号 + 都道府県 + 住所 をまとめて表示 -->
      <template v-if="readOnly">
        <div class="address-field address-field-full">
          <div class="readonly-text">
            {{ fullAddress }}
          </div>
        </div>
      </template>

      <!-- 編集モード時のフィールド群 -->
      <template v-else>
        <!-- 郵便番号 -->
        <div class="address-field address-field-postal-code">
          <div class="postal-code-input-wrapper">
            <TextInput
              label=""
              :value="postalCode"
              :placeholder="postalCodePlaceholder"
              :disabled="disabled"
              @input="onPostalCodeInput"
              class="postal-code-input"
            />
          </div>
        </div>

        <!-- 住所検索ボタン -->
        <div class="address-field address-field-search-button">
          <Button
            type="filled"
            colorType="secondary"
            size="sm"
            label="住所検索"
            :disabled="!isPostalCodeValid || disabled"
            @click="handleSearchAddress"
            class="search-address-button"
          />
        </div>

        <!-- 都道府県 -->
        <div class="address-field address-field-prefecture">
          <treeselect
            :value="normalizedPrefecture"
            :options="prefectureOptionsForTree"
            :disabled="disabled"
            :multiple="false"
            :clearable="true"
            :placeholder="prefecturePlaceholder"
            noOptionsText="なし"
            @input="onPrefectureChange"
          />
        </div>

        <!-- 住所 -->
        <div class="address-field address-field-address">
          <TextInput
            label=""
            :value="address"
            :placeholder="addressPlaceholder"
            :disabled="disabled"
            @input="onAddressInput"
          />
        </div>
      </template>
    </div>
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    <InputBottomDivider v-if="readOnly" />
  </div>
</template>

<script>
import FormLabel from "./FormLabel.vue";
import InputBottomDivider from "./InputBottomDivider.vue";
import TextInput from "./TextInput.vue";
import Button from "../Button.vue";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import { PostcodeApiFactory } from "@/api/postcode/apiFactory";

const postcodeApi = PostcodeApiFactory.get("postcode");

// 都道府県オプション
const PREFECTURE_OPTIONS = [
  { value: "北海道", label: "北海道" },
  { value: "青森県", label: "青森県" },
  { value: "岩手県", label: "岩手県" },
  { value: "宮城県", label: "宮城県" },
  { value: "秋田県", label: "秋田県" },
  { value: "山形県", label: "山形県" },
  { value: "福島県", label: "福島県" },
  { value: "茨城県", label: "茨城県" },
  { value: "栃木県", label: "栃木県" },
  { value: "群馬県", label: "群馬県" },
  { value: "埼玉県", label: "埼玉県" },
  { value: "千葉県", label: "千葉県" },
  { value: "東京都", label: "東京都" },
  { value: "神奈川県", label: "神奈川県" },
  { value: "新潟県", label: "新潟県" },
  { value: "富山県", label: "富山県" },
  { value: "石川県", label: "石川県" },
  { value: "福井県", label: "福井県" },
  { value: "山梨県", label: "山梨県" },
  { value: "長野県", label: "長野県" },
  { value: "岐阜県", label: "岐阜県" },
  { value: "静岡県", label: "静岡県" },
  { value: "愛知県", label: "愛知県" },
  { value: "三重県", label: "三重県" },
  { value: "滋賀県", label: "滋賀県" },
  { value: "京都府", label: "京都府" },
  { value: "大阪府", label: "大阪府" },
  { value: "兵庫県", label: "兵庫県" },
  { value: "奈良県", label: "奈良県" },
  { value: "和歌山県", label: "和歌山県" },
  { value: "鳥取県", label: "鳥取県" },
  { value: "島根県", label: "島根県" },
  { value: "岡山県", label: "岡山県" },
  { value: "広島県", label: "広島県" },
  { value: "山口県", label: "山口県" },
  { value: "徳島県", label: "徳島県" },
  { value: "香川県", label: "香川県" },
  { value: "愛媛県", label: "愛媛県" },
  { value: "高知県", label: "高知県" },
  { value: "福岡県", label: "福岡県" },
  { value: "佐賀県", label: "佐賀県" },
  { value: "長崎県", label: "長崎県" },
  { value: "熊本県", label: "熊本県" },
  { value: "大分県", label: "大分県" },
  { value: "宮崎県", label: "宮崎県" },
  { value: "鹿児島県", label: "鹿児島県" },
  { value: "沖縄県", label: "沖縄県" },
];

export default {
  name: "AddressInput",
  components: {
    FormLabel,
    InputBottomDivider,
    TextInput,
    Button,
    Treeselect,
  },
  props: {
    /**
     * 郵便番号
     * @type {String}
     * @default ""
     */
    postalCode: {
      type: String,
      default: "",
    },
    /**
     * 都道府県
     * @type {String}
     * @default ""
     */
    prefecture: {
      type: String,
      default: "",
    },
    /**
     * 住所
     * @type {String}
     * @default ""
     */
    address: {
      type: String,
      default: "",
    },
    /**
     * 郵便番号ラベル
     * @type {String}
     * @default "郵便番号"
     */
    postalCodeLabel: {
      type: String,
      default: "郵便番号",
    },
    /**
     * 都道府県ラベル
     * @type {String}
     * @default "都道府県"
     */
    prefectureLabel: {
      type: String,
      default: "都道府県",
    },
    /**
     * 住所ラベル
     * @type {String}
     * @default "住所"
     */
    addressLabel: {
      type: String,
      default: "住所",
    },
    /**
     * 郵便番号プレースホルダー
     * @type {String}
     * @default "例）1234567"
     */
    postalCodePlaceholder: {
      type: String,
      default: "例）1234567",
    },
    /**
     * 都道府県プレースホルダー
     * @type {String}
     * @default "選択してください"
     */
    prefecturePlaceholder: {
      type: String,
      default: "選択してください",
    },
    /**
     * 住所プレースホルダー
     * @type {String}
     * @default "例）新宿区新宿4-1-6"
     */
    addressPlaceholder: {
      type: String,
      default: "例）新宿区新宿4-1-6",
    },
    /**
     * 必須項目かどうか
     * @type {Boolean}
     * @default false
     */
    isRequired: {
      type: Boolean,
      default: false,
    },
    /**
     * 読み取り専用かどうか
     * @type {Boolean}
     * @default false
     */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * 無効化されているかどうか
     * @type {Boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * エラーメッセージ
     * @type {String}
     * @default ""
     */
    errorMessage: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      prefectureOptions: PREFECTURE_OPTIONS,
      isSearching: false,
    };
  },
  computed: {
    displayPostalCode() {
      if (!this.postalCode || this.postalCode === "") {
        return "-";
      }
      return this.postalCode;
    },
    displayPrefecture() {
      if (!this.prefecture || this.prefecture === "") {
        return "-";
      }
      return this.prefecture;
    },
    displayAddress() {
      if (!this.address || this.address === "") {
        return "-";
      }
      return this.address;
    },
    isPostalCodeValid() {
      return (
        this.postalCode &&
        this.postalCode.length === 7 &&
        /^[0-9]{7}$/.test(this.postalCode)
      );
    },
    prefectureOptionsForTree() {
      return this.prefectureOptions.map(pref => ({
        id: pref.value,
        label: pref.label,
      }));
    },
    normalizedPrefecture() {
      // 空文字列やnullの場合はnullを返す（Treeselectでプレースホルダーを表示するため）
      if (!this.prefecture || this.prefecture === "") {
        return null;
      }
      return this.prefecture;
    },
    /**
     * readOnly表示用のフル住所（郵便番号 + 都道府県 + 住所 をスペース区切りで連結）
     */
    fullAddress() {
      const parts = [];
      if (this.postalCode && this.postalCode !== "") {
        parts.push("〒" + this.postalCode);
      }
      if (this.prefecture && this.prefecture !== "") {
        this.prefecture;
        parts.push(this.prefecture);
      }
      if (this.address && this.address !== "") {
        parts.push(this.address);
      }
      return parts.length > 0 ? parts.join("　") : "-";
    },
  },
  methods: {
    onPostalCodeInput(value) {
      this.$emit("update:postalCode", value);
    },
    onPrefectureChange(value) {
      this.$emit("update:prefecture", value || "");
    },
    onAddressInput(value) {
      this.$emit("update:address", value);
    },
    async handleSearchAddress() {
      if (!this.isPostalCodeValid || this.isSearching) {
        return;
      }

      this.isSearching = true;
      try {
        const res = await postcodeApi.show(this.postalCode);
        if (res.data && res.data.length > 0) {
          const addressData = res.data[0];
          // 都道府県を抽出（住所の最初から都道府県名を探す）
          const prefecture = this.extractPrefecture(addressData.allAddress);
          // 都道府県を除いた住所を取得
          const address = addressData.allAddress.replace(prefecture, "").trim();

          this.$emit("update:prefecture", prefecture);
          this.$emit("update:address", address);
        } else {
          this.$vs.notify({
            title: "エラー",
            text: "住所を取得できませんでした。郵便番号をご確認ください。",
            color: "danger",
          });
        }
      } catch (error) {
        this.$vs.notify({
          title: "エラー",
          text: "住所を取得できませんでした。郵便番号をご確認ください。",
          color: "danger",
        });
      } finally {
        this.isSearching = false;
      }
    },
    extractPrefecture(fullAddress) {
      // 都道府県リストから住所に含まれる都道府県を探す
      for (const pref of this.prefectureOptions) {
        if (fullAddress.startsWith(pref.value)) {
          return pref.value;
        }
      }
      return "";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.address-input-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  .address-input-fields {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;

    .address-field {
      display: flex;
      flex-direction: column;
      .postal-code-input-wrapper {
        width: 120px;
      }

      &.address-field-postal-code {
        ::v-deep .postal-code-input {
          .text-input-container {
            width: 100%;
            margin-top: 0;
          }
        }
      }

      &.address-field-search-button {
        flex-shrink: 0;
        display: flex;
        align-items: flex-end;
        padding-bottom: 0;

        .search-address-button {
          white-space: nowrap;
        }
      }

      &.address-field-prefecture {
        width: 150px;
        flex-shrink: 0;
      }

      &.address-field-address {
        flex: 1;
      }

      .readonly-text {
        padding: 0rem 0.75rem;
        font-family: $aisea-font-family;
        font-size: 16px;
        font-weight: 400;
        color: $aisea-dark-gray;
        line-height: 1.5;
        min-height: 26px;
      }

      ::v-deep .vue-treeselect {
        .vue-treeselect__control {
          border: 1px solid $aisea-mouse-gray;
          border-radius: 6px;
          min-height: 36px;
          transition: border-color 0.3s ease, box-shadow 0.3s ease;
        }

        .vue-treeselect__control--focused {
          border-color: #2d9cdb;
          box-shadow: 0 0 0 2px rgba(45, 156, 219, 0.1);
        }

        &.vue-treeselect--disabled {
          .vue-treeselect__control {
            background-color: #f5f5f5;
            border-color: $aisea-light-gray;
            cursor: not-allowed;
          }
        }
      }
    }

    &.has-error {
      .address-field {
        // TextInput（郵便番号・住所）
        ::v-deep .text-input-container {
          .text-input {
            border-color: $aisea-red;
            background-color: rgba($aisea-red, 0.1);
          }
        }

        // Treeselect（都道府県）
        ::v-deep .vue-treeselect {
          .vue-treeselect__control {
            border-color: $aisea-red;
            background-color: rgba($aisea-red, 0.1);
          }
        }
      }
    }
  }

  .error-message {
    margin-top: 0.25rem;
    padding-left: 0.25rem;
    font-family: $aisea-font-family;
    font-size: 12px;
    font-weight: 400;
    color: $aisea-red;
    line-height: 1.5;
  }
}
</style>
